import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(() => {
    if (typeof window !== 'undefined' && (window as any).__deferredPwaPrompt) {
      return (window as any).__deferredPwaPrompt;
    }
    return null;
  });
  const [isInstalled, setIsInstalled] = useState<boolean>(false);
  const [isInstallable, setIsInstallable] = useState<boolean>(false);
  const [platform, setPlatform] = useState<'ios' | 'android' | 'desktop'>('desktop');
  const [isInAppBrowser, setIsInAppBrowser] = useState<boolean>(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const ua = window.navigator.userAgent.toLowerCase();

    // 1. Detectar plataforma
    if (/iphone|ipad|ipod/.test(ua)) {
      setPlatform('ios');
    } else if (/android/.test(ua)) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // 2. Detectar si está en un WebView / In-App Browser (WhatsApp, Instagram, FB, etc.)
    const isFbOrIgOrWa =
      ua.includes('fban') ||
      ua.includes('fbav') ||
      ua.includes('instagram') ||
      ua.includes('whatsapp') ||
      ua.includes('messenger') ||
      ua.includes('tiktok') ||
      (ua.includes('wv') && ua.includes('chrome'));
    setIsInAppBrowser(isFbOrIgOrWa);

    // 3. Detectar si ya se está ejecutando como app instalada (standalone)
    // NOTA: NO usar document.referrer porque causa falsos positivos en Android
    const isInsideIframe = window.self !== window.top;
    const isStandalone = !isInsideIframe && (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone === true
    );
    setIsInstalled(isStandalone);

    // 4. Si el prompt ya fue capturado globalmente en window
    if ((window as any).__deferredPwaPrompt) {
      setDeferredPrompt((window as any).__deferredPwaPrompt);
      setIsInstallable(true);
      
      // Auto-disparar si la URL trae autoInstall=1
      if (window.location.search.includes('autoInstall=1')) {
        try {
          (window as any).__deferredPwaPrompt.prompt();
        } catch {
          // Fallback
        }
      }
    }

    // Manejador del evento estándar
    const handlePrompt = (e: Event) => {
      e.preventDefault();
      const promptEvent = e as BeforeInstallPromptEvent;
      (window as any).__deferredPwaPrompt = promptEvent;
      setDeferredPrompt(promptEvent);
      setIsInstallable(true);

      // Auto-disparar si la URL trae autoInstall=1
      if (window.location.search.includes('autoInstall=1')) {
        try {
          promptEvent.prompt();
        } catch {
          // Fallback
        }
      }
    };

    // Manejador del evento personalizado emitido desde index.html
    const handleCustomPrompt = (e: any) => {
      if (e.detail) {
        setDeferredPrompt(e.detail);
        setIsInstallable(true);
      }
    };

    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      (window as any).__deferredPwaPrompt = null;
    };

    window.addEventListener('beforeinstallprompt', handlePrompt);
    window.addEventListener('pwa-prompt-ready', handleCustomPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handlePrompt);
      window.removeEventListener('pwa-prompt-ready', handleCustomPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  // Abrir en Chrome nativo si está dentro de WhatsApp o navegador embebido
  const openInChrome = useCallback(() => {
    if (typeof window === 'undefined') return;
    const currentUrl = window.location.href;
    const cleanUrl = currentUrl.replace(/^https?:\/\//, '');

    // Intent directo para Chrome en Android
    const chromeIntent = `intent://${cleanUrl}#Intent;scheme=https;package=com.android.chrome;end`;
    try {
      window.location.href = chromeIntent;
    } catch {
      window.open(currentUrl, '_system');
    }
  }, []);

  const triggerInstall = useCallback(async (): Promise<'accepted' | 'dismissed' | 'manual'> => {
    // Si estamos dentro de un iframe (como el preview móvil de AI Studio),
    // los navegadores bloquean el prompt de instalación PWA. Abrimos en ventana principal
    // para que Chrome lance la instalación nativa directa con un clic.
    const isInsideIframe = typeof window !== 'undefined' && window.self !== window.top;
    if (isInsideIframe) {
      const targetUrl = window.location.origin + '/?source=pwa&autoInstall=1';
      window.open(targetUrl, '_blank');
      return 'accepted';
    }

    const promptEvent = deferredPrompt || (typeof window !== 'undefined' ? (window as any).__deferredPwaPrompt : null);

    if (!promptEvent) {
      return 'manual';
    }

    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;
      if (choice.outcome === 'accepted') {
        setIsInstalled(true);
        setIsInstallable(false);
        setDeferredPrompt(null);
        if (typeof window !== 'undefined') {
          (window as any).__deferredPwaPrompt = null;
        }
      }
      return choice.outcome;
    } catch {
      return 'manual';
    }
  }, [deferredPrompt]);

  return {
    isInstallable: isInstallable || (!isInstalled && platform === 'android'),
    isInstalled,
    platform,
    isInAppBrowser,
    openInChrome,
    triggerInstall,
    hasNativePrompt: !!deferredPrompt || (typeof window !== 'undefined' && !!(window as any).__deferredPwaPrompt),
  };
}
